import { useContext, useEffect, useMemo, useState } from "react";
import PopUp from "./PopUp";
import { AppContext } from "../App";
import Table from "./ui/Table";
import SplitIcon from "../icons/SplitIcon";
import MergeIcon from "../icons/MergeIcon";
import SearchIcon from "../icons/SearchIcon";
import FilterIcon from "../icons/FilterIcon";
import DateQuickPicker from "./ui/DateQuickPicker";
import { useAppStore } from "../store/useAppStore";
import api from "./api/api";

function OrdersList({ onClose, highlightedOrderId, setHighlightedOrderId }) {
	const {
		config,
		apiSettings,
		openOrder,
		orders,
		updateOrder,
		loadOrders,
		setConfirm,
		setSplitPopup,
		ordersFilter,
		setOrdersFilter,
		setLoading,
	} = useContext(AppContext);
	const { setSplittedOrders, setSplittedOrdersCount } = useAppStore();
	const [typeFilter, setTypeFilter] = useState("all");
	const [originFilter, setOriginFilter] = useState("all");
	const [statusFilter, setStatusFilter] = useState("all");
	const [paidFilter, setPaidFilter] = useState("all");
	const [ordersSearch, setOrdersSearch] = useState("");
	const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
	const [selectedOrderIds, setSelectedOrderIds] = useState([]);

	useEffect(() => {
		loadOrders();
	}, []);

	useEffect(() => {
		if (!highlightedOrderId) return;
		setTypeFilter("all");
		setOriginFilter("all");
		setStatusFilter("all");
		setPaidFilter("all");
		setOrdersSearch("");
		setShowAdvancedFilters(false);
	}, [highlightedOrderId]);

	const getOriginLabel = (origin) => {
		const map = {
			pos: "POS",
			website: "Website",
			selfordering: "Self Ordering",
			kiosk: "Kiosk",
			waiter: "Waiter",
			menu: "Menu",
			backend: "Backend",
			glovo: "Glovo",
			deliveroo: "Deliveroo",
			justeat: "Just Eat",
			ubereats: "Uber Eats",
			tracking: "Tracking",
			kitchenmonitor: "Kitchen Monitor",
			storedisplay: "Store Display",
		};
		if (map[origin]) return map[origin];
		return origin
			.split("_")
			.filter(Boolean)
			.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
			.join(" ");
	};

	const getStatusLabel = (status) => {
		if (status == "unconfirmed") return "Non confermato";
		if (status == "confirmed") return "Confermato";
		if (status == "payment_error") return "Errore pagamento";
		if (status == "awaiting_payment") return "In attesa di pagamento";
		if (status == "awaiting_cash_payment") return "In attesa pagamento contanti";
		if (status == "archived") return "Annullato";
		if (status == "cancel_request") return "Richiesta annullo";
		if (status == "preparation") return "In preparazione";
		if (status == "filled") return "Evaso";
		if (status == "ready") return "Pronto";
		if (status == "delivering") return "In consegna";
		return status;
	};

	const getPaidState = (order) => {
		if (order.status == "payment_error" || order.status == "awaiting_payment") return "error";
		if (order.payment_method?.prepaid == 1) return "yes";
		if (order.payments?.some(p => p.gateway)) return "yes";
		return "no";
	};

	const typeOptions = useMemo(() => {
		const options = [
			{ value: "shop", label: "Sala" },
			{ value: "takeaway", label: "Asporto" },
			{ value: "delivery", label: "Delivery" },
		];

		const hasType = new Set(options.map((option) => option.value));
		for (const order of orders || []) {
			const value = String(order?.delivery_type || "").trim();
			if (value && !hasType.has(value)) {
				options.push({
					value,
					label: value.charAt(0).toUpperCase() + value.slice(1),
				});
				hasType.add(value);
			}
		}
		return options;
	}, [orders]);

	const getDeliveryTypeLabel = (deliveryType) => {
		const option = typeOptions.find((item) => item.value === deliveryType);
		if (option) return option.label;
		const value = String(deliveryType || "").trim();
		return value ? value.charAt(0).toUpperCase() + value.slice(1) : "";
	};

	const originOptions = useMemo(() => {
		const knownOrigins = [
			"pos",
			"backend",
			"website",
			"selfordering",
			"menu",
			"kiosk",
			"waiter",
			"tracking",
			"kitchenmonitor",
			"storedisplay",
			"glovo",
			"deliveroo",
			"justeat",
			"ubereats",
		];
		const mandantOriginMap = {
			website: "website",
			selfordering: "selfordering",
			dmenu: "menu",
			kiosk: "kiosk",
			waiter_app: "waiter",
			tracking: "tracking",
			kitchenmonitor: "kitchenmonitor",
			store_display: "storedisplay",
			glovo: "glovo",
			deliveroo: "deliveroo",
			justeat: "justeat",
			ubereats: "ubereats",
		};

		const set = new Set();
		const add = (value) => {
			const parsed = String(value || "").trim();
			if (parsed) set.add(parsed);
		};

		knownOrigins.forEach(add);
		Object.entries(mandantOriginMap).forEach(([flag, origin]) => {
			if (String(apiSettings?.mandant?.[flag] ?? "0") === "1") add(origin);
		});
		(apiSettings?.mandant?.clients || []).forEach((client) => add(client?.origin));
		Object.entries(apiSettings?.plugins || {}).forEach(([origin, plugin]) => {
			add(origin);
			if (String(plugin?.active ?? plugin?.enabled ?? "0") === "1") add(origin);
		});
		(orders || []).forEach((order) => add(order?.origin));

		return Array.from(set);
	}, [apiSettings, orders]);

	const statusOptions = useMemo(() => {
		const orderedStatuses = [
			"unconfirmed",
			"confirmed",
			"preparation",
			"ready",
			"delivering",
			"filled",
			"cancel_request",
			"awaiting_payment",
			"awaiting_cash_payment",
			"payment_error",
			"archived",
		];
		const set = new Set(orderedStatuses);
		(orders || []).forEach((order) => {
			const status = String(order?.status || "").trim();
			if (status) set.add(status);
		});
		return Array.from(set);
	}, [orders]);

	const normalizedSearch = ordersSearch.trim().toLowerCase();
	const filteredOrders = useMemo(() => {
		return orders.filter((order) => {
			if (typeFilter !== "all" && String(order?.delivery_type || "") !== typeFilter) return false;
			if (originFilter !== "all" && String(order?.origin || "") !== originFilter) return false;
			if (statusFilter !== "all" && String(order?.status || "") !== statusFilter) return false;
			if (paidFilter !== "all" && getPaidState(order) !== paidFilter) return false;
			if (!normalizedSearch) return true;
			const id = String(order?.id ?? "");
			const number = String(order?.number ?? "");
			const name = String(order?.name ?? "");
			const surname = String(order?.surname ?? "");
			const fullName = [name, surname].filter(Boolean).join(" ");
			const email = String(order?.email ?? order?.customer?.email ?? "");
			return [id, number, name, surname, fullName, email].some((value) =>
				value.toLowerCase().includes(normalizedSearch)
			);
		});
	}, [orders, normalizedSearch, typeFilter, originFilter, statusFilter, paidFilter]);
	const hasSearch = normalizedSearch.length > 0;
	const hasActiveFilters =
		typeFilter !== "all" || originFilter !== "all" || statusFilter !== "all" || paidFilter !== "all";
	const selectedOrders = useMemo(() => {
		return selectedOrderIds.map((id) => orders.find((order) => order.id === id)).filter(Boolean);
	}, [orders, selectedOrderIds]);
	const selectedDeliveryType = selectedOrders[0]?.delivery_type || null;
	const selectedDeliveryTypeLabel = getDeliveryTypeLabel(selectedDeliveryType);
	const isOrderSelectionDisabled = (order) => {
		if (!selectedDeliveryType) return false;
		if (selectedOrderIds.includes(order.id)) return false;
		return order.delivery_type !== selectedDeliveryType;
	};
	const toggleOrderSelection = (order) => {
		if (order.table_id) return;
		if (selectedOrderIds.includes(order.id)) {
			setSelectedOrderIds((prev) => prev.filter((id) => id !== order.id));
			return;
		}
		if (isOrderSelectionDisabled(order)) {
			setConfirm({
				title: "Selezione non compatibile",
				message: "Puoi unire solo ordini dello stesso tipo.",
			});
			return;
		}
		setSelectedOrderIds((prev) => [...prev, order.id]);
	};

	const executeMergeSelectedOrders = async () => {
		setLoading(true);
		const res = await api.postProtected("/orders/merge/", { ids: selectedOrderIds });
		setLoading(false);
		if (res.success == 0) {
			alert(res.error);
		} else {
			setSelectedOrderIds([]);
			loadOrders();
		}
	};

	const mergeSelectedOrders = () => {
		setConfirm({
			title: "Unire gli ordini?",
			message:
				"Verranno uniti " +
				selectedOrderIds.length +
				" ordini" +
				(selectedDeliveryTypeLabel ? " " + selectedDeliveryTypeLabel.toLowerCase() : "") +
				".",
			onConfirm: executeMergeSelectedOrders,
		});
	};

	const hasCustomerColumn = useMemo(() => {
		return (orders || []).some((order) => {
			return Boolean(
				(order?.customer_id && Number(order.customer_id) > 0) ||
				String(order?.name || "").trim() ||
				String(order?.surname || "").trim() ||
				String(order?.email || "").trim() ||
				String(order?.mobile || "").trim()
			);
		});
	}, [orders]);

	useEffect(() => {
		if (!highlightedOrderId) return;
		const timeoutId = window.setTimeout(() => {
			const row = document.querySelector(
				`.grid-table-orders .table-cell.row-highlighted[data-row-id="${highlightedOrderId}"]`,
			);
			if (row) row.scrollIntoView({ behavior: "smooth", block: "center" });
		}, 120);

		const clearHighlightId = window.setTimeout(() => {
			if (typeof setHighlightedOrderId === "function") setHighlightedOrderId(null);
		}, 2600);

		return () => {
			window.clearTimeout(timeoutId);
			window.clearTimeout(clearHighlightId);
		};
	}, [highlightedOrderId, filteredOrders, setHighlightedOrderId]);

	return (
		<PopUp
			size="large"
			options={{
				onClose: onClose,
				title: (
					<div className="orders-list-title-row">
						<span className="orders-list-title-with-badge">
							<span className="orders-list-title-badge">{filteredOrders.length}</span>
							Ordini
						</span>
						<DateQuickPicker value={ordersFilter} onChange={setOrdersFilter} />
					</div>
				),
				headerButtons: (
					<div className="orders-list-header-controls">
						<div className="orders-list-search-wrap">
							<input
								type="search"
								value={ordersSearch}
								onChange={(e) => setOrdersSearch(e.target.value)}
								placeholder="Cerca cliente o ID ordine"
							/>
							<span className="orders-list-search-icon" aria-hidden="true">
								<SearchIcon />
							</span>
						</div>
						<button
							type="button"
							className={"orders-list-filters-toggle" + (showAdvancedFilters ? " active" : "")}
							onClick={() => setShowAdvancedFilters((prev) => !prev)}
							aria-label="Mostra o nascondi filtri"
							aria-pressed={showAdvancedFilters}
						>
							<FilterIcon />
						</button>
					</div>
				),
				content: (
					<div className="orders-list-content">
						{showAdvancedFilters && (
							<div className="orders-list-table-filters">
								<select
									className="orders-list-filter-select"
									value={typeFilter}
									onChange={(e) => setTypeFilter(e.target.value)}
								>
									<option value="all">Tipo</option>
									{typeOptions.map((type) => (
										<option key={type.value} value={type.value}>
											{"Tipo: " + type.label}
										</option>
									))}
								</select>
								<select
									className="orders-list-filter-select"
									value={originFilter}
									onChange={(e) => setOriginFilter(e.target.value)}
								>
									<option value="all">Origine</option>
									{originOptions.map((origin) => (
										<option key={origin} value={origin}>
											{"Origine: " + getOriginLabel(origin)}
										</option>
									))}
								</select>
								<select
									className="orders-list-filter-select"
									value={statusFilter}
									onChange={(e) => setStatusFilter(e.target.value)}
								>
									<option value="all">Stato</option>
									{statusOptions.map((status) => (
										<option key={status} value={status}>
											{"Stato: " + getStatusLabel(status)}
										</option>
									))}
								</select>
								<select
									className="orders-list-filter-select"
									value={paidFilter}
									onChange={(e) => setPaidFilter(e.target.value)}
								>
									<option value="all">Pagato</option>
									<option value="yes">Pagato: si</option>
									<option value="no">Pagato: no</option>
									<option value="error">Pagato: errore</option>
								</select>
							</div>
						)}
						{(hasSearch || hasActiveFilters) && (
							<div className="orders-list-search-results-label">risultati di ricerca</div>
						)}
						{selectedOrderIds.length > 1 && (
							<div className="orders-merge-bar">
								<div className="orders-merge-summary">
									<span className="orders-merge-count">{selectedOrderIds.length}</span>
									<span className="orders-merge-label">ordini selezionati</span>
									{selectedDeliveryTypeLabel && (
										<span className="orders-merge-type">{selectedDeliveryTypeLabel}</span>
									)}
								</div>
								<div className="orders-merge-actions">
									<button className="button button-1" onClick={mergeSelectedOrders}>
										<MergeIcon /> Unisci ordini
									</button>
									<button className="button orders-merge-cancel" onClick={() => setSelectedOrderIds([])}>
										Annulla selezione
									</button>
								</div>
							</div>
						)}
						<div className="products-admin-table orders-admin-table">
							<Table
								header={true}
								config={config}
								type="orders"
								highlightedRowId={highlightedOrderId}
								cols={[
									{
										id: "merge_select",
										label: "",
										type: "checkbox",
										align: "center",
										width: "40px",
										isChecked: (order) => selectedOrderIds.includes(order.id),
										isHidden: (order) => Boolean(order.table_id) || Boolean(order.merge_id),
										isDisabled: isOrderSelectionDisabled,
										getTitle: (order) =>
											isOrderSelectionDisabled(order)
												? "Puoi unire solo ordini " + selectedDeliveryTypeLabel
												: undefined,
										onChange: toggleOrderSelection,
									},
									{ id: "id", label: "ID", type: "id", align: "center", width: "70px" },
									{
										id: "delivery_type",
										label: "Tipo",
										type: "delivery_type",
										align: "center",
										width: "82px",
									},
									{
										id: "delivery_date",
										label: "Ora",
										type: "delivery_date",
										align: "center",
										width: "78px",
									},
									{
										id: "origin",
										label: "Origine",
										type: "origin",
										align: "center",
										width: "92px",
									},
									{
										id: "customer",
										label: "Cliente",
										type: "customer",
										width: "120px",
										align: "left",
										visible: hasCustomerColumn,
									},
									{ id: "user", label: "Cameriere", type: "user", align: "center", width: "92px" },
									{
										id: "info",
										label: "Info",
										type: "info",
										align: "left",
										width: "minmax(220px, 2fr)",
									},
									{
										id: "thermal_print_results",
										label: "Comande",
										type: "thermal_print_results",
										align: "left",
										width: "minmax(190px, 1.25fr)",
									},
									{ id: "total", label: "Totale", type: "eur", align: "right", width: "96px" },
									{
										id: "payed",
										label: "Pagato",
										type: "prepaid",
										align: "center",
										width: "74px",
									},
									{
										id: "buttons",
										label: "",
										type: "buttons",
										align: "right",
										width: "max-content",
										buttons: [
											{
												icon: <SplitIcon />,
												onClick: (order) =>
													setSplitPopup({ mode: "list", order: order }),
												// niente divisione su un ordine gia' diviso alla romana ne' sulle sue quote (parent_id)
												visible: (order) => !(order.childs?.length > 0) && !order.parent_id,
											},
											{
												label: "Seleziona",
												className: "button-1 orders-select-fit",
												onClick: (order) => {
													setSplittedOrders([]);
													setSplittedOrdersCount(0);
													openOrder(order);
													onClose();
												},
												visible: (order) =>
													!order.childs || order.childs.length == 0,
											},
											{
												label: "Completa",
												onClick: (order) => {
													setConfirm({
														title: "Sei sicuro?",
														message:
															"Procedendo l'ordine non sarà più modificabile",
														onConfirm: async () => {
															updateOrder(order, { bill: 1 });
														},
													});
												},
												visible:
													apiSettings.mandant.fiscal_printers != 1 &&
													apiSettings.pos.fast_checkout == 1,
											},
										],
									},
								]}
								rows={filteredOrders}
							/>
						</div>
					</div>
				),
			}}
		/>
	);
}

export default OrdersList;
